import React, { useState, useEffect } from 'react';
import { Loader, CheckCircle, XCircle, Trophy } from 'lucide-react';
import StepTracker from './StepTracker';
import { BACKEND_URL } from '../config';

const OnboardingCompletePage = () => {
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [discordUsername, setDiscordUsername] = useState('');
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const savedToken = sessionStorage.getItem('onboarding_token');

    const finalToken = tokenParam || savedToken;

    if (finalToken) {
      setToken(finalToken);
    } else {
      setError('Token no encontrado.');
    }
  }, []);

  const handleClaimRole = async () => {
    if (!token) {
      setError('Token no encontrado.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'claim-role', token: token }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        if (data.discordUsername) {
          setDiscordUsername(data.discordUsername);
        }
        sessionStorage.removeItem('onboarding_token');
        setTimeout(() => (window.location.href = 'https://www.nosoyliquidez.com'), 3000);
      } else {
        setError(data.error || 'Error al reclamar el role');
      }
    } catch {
      setError('Error al procesar la solicitud. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4 py-8 overflow-x-hidden relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-700"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/logo-nsl.png" alt="NSL Logo" className="h-24 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
            Onboarding NSL
          </h1>
        </div>

        <StepTracker currentStep={7} />

        <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-600/20 rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden">
          <div className="p-6 sm:p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500/10 rounded-full mb-6 animate-bounce">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">¡Role Asignado!</h2>
                <p className="text-gray-300 mb-2 text-lg">
                  Tu role de <span className="text-yellow-500 font-semibold">Estudiante Privado Oficial</span> ha sido asignado exitosamente
                </p>
                {discordUsername && (
                  <div className="mt-6 max-w-md mx-auto p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <p className="text-green-400 text-base">
                      Usuario: <span className="font-bold text-green-300">{discordUsername}</span>
                    </p>
                  </div>
                )}
                <p className="text-gray-400 text-sm mt-6">Redirigiendo a NSL...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/10 rounded-full mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
                <p className="text-red-400 mb-6">{error}</p>
                <button
                  onClick={handleClaimRole}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Reintentar
                </button>
              </div>
            ) : isLoading ? (
              <div className="text-center py-8">
                <Loader className="w-16 h-16 text-yellow-500 animate-spin mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-white mb-4">Asignando tu Role...</h2>
                <p className="text-gray-400">Estamos asignando tu role de Estudiante Privado Oficial.</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-yellow-500/10 rounded-full mb-6 animate-bounce">
                  <Trophy className="w-16 h-16 text-yellow-500" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                  ¡Bienvenido Estudiante Privado!
                </h2>
                <p className="text-xl text-white mb-2">Onboarding Terminado</p>
                <p className="text-gray-300 mb-8 text-lg">
                  Has completado todos los pasos del onboarding exitosamente.
                </p>

                <div className="max-w-md mx-auto mb-8 p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <p className="text-gray-300 text-sm mb-3">
                    🎉 <span className="font-semibold text-white">¡Felicidades!</span> Estás a un click de recibir tu role oficial de Estudiante Privado en Discord.
                  </p>
                  <p className="text-gray-400 text-xs">
                    Al hacer click en el botón de abajo, se te asignará automáticamente el role en el servidor de NSL.
                  </p>
                </div>

                <button
                  onClick={handleClaimRole}
                  disabled={!token}
                  className={`w-full max-w-md mx-auto font-bold py-5 px-8 rounded-xl transition-all duration-300 transform shadow-2xl flex items-center justify-center gap-3 text-lg ${
                    !token
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black hover:scale-105 hover:shadow-yellow-500/50'
                  }`}
                >
                  <Trophy className="w-6 h-6" />
                  Reclamar tu Role
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© {currentYear} No Soy Liquidez. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingCompletePage;